import { RefObject, useEffect, useRef } from 'react';

// Ambient typings for the YouTube IFrame API, scoped to the player hook.
declare namespace YT {
  interface Player {
    loadVideoById: (videoId: string) => void;
    getPlayerState: () => number;
    playVideo: () => void;
    destroy: () => void;
  }

  interface PlayerEvent {
    target: Player;
    data?: unknown;
  }
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        element: HTMLElement | string,
        options: {
          videoId: string;
          host?: string;
          playerVars?: {
            rel?: number;
            controls?: number;
            fs?: number;
            iv_load_policy?: number;
            origin?: string;
            widget_referrer?: string;
            enablejsapi?: number;
            disablekb?: number;
            playsinline?: number;
            autohide?: number;
            playlist?: string;
            loop?: number;
            mute?: number;
          };
          events?: {
            onReady?: (event: YT.PlayerEvent) => void;
            onError?: (event: YT.PlayerEvent) => void;
          };
        },
      ) => YT.Player;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Load the IFrame API exactly once, regardless of how many players mount.
// Resolves as soon as `window.YT.Player` is usable.
let apiReadyPromise: Promise<void> | null = null;
const loadYouTubeIframeApi = (): Promise<void> => {
  if (window.YT?.Player) return Promise.resolve();
  if (apiReadyPromise) return apiReadyPromise;

  apiReadyPromise = new Promise<void>(resolve => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
  });
  return apiReadyPromise;
};

interface UseYouTubePlayerArgs {
  // The 11-char video id to play, or null/'' for "no player" (locked content or
  // nothing selected).
  videoId: string | null;
  // React-owned wrapper. The YT iframe mounts as a *child* of this node, not the
  // node itself: the API replaces the element it's given, which would otherwise
  // leave React removing a node that no longer exists (removeChild NotFoundError).
  hostRef: RefObject<HTMLElement>;
  mute?: boolean;
}

export const useYouTubePlayer = ({
  videoId,
  hostRef,
  mute = false,
}: UseYouTubePlayerArgs): void => {
  const playerRef = useRef<YT.Player | null>(null);

  useEffect(() => {
    const destroyPlayer = () => {
      if (!playerRef.current) return;
      try {
        playerRef.current.destroy();
      } catch (error) {
        console.error('[useYouTubePlayer] error destroying player', error);
      }
      playerRef.current = null;
    };

    if (!videoId) {
      destroyPlayer();
      return;
    }

    let cancelled = false;

    const initPlayer = () => {
      if (cancelled || !window.YT?.Player) return;
      const host = hostRef.current;
      if (!host) return;

      // Replace any player from a previous selection, then give YT a fresh
      // throwaway child to swap for its iframe.
      destroyPlayer();
      host.innerHTML = '';
      const target = document.createElement('div');
      host.appendChild(target);

      try {
        new window.YT.Player(target, {
          videoId,
          playerVars: {
            controls: 1,
            rel: 0,
            iv_load_policy: 3,
            enablejsapi: 1,
            autohide: 1,
            loop: 1,
            mute: mute ? 1 : 0,
          },
          events: {
            onReady: event => {
              if (cancelled) {
                event.target.destroy();
                return;
              }
              playerRef.current = event.target;
              try {
                event.target.playVideo();
              } catch (error) {
                console.error(
                  '[useYouTubePlayer] error calling playVideo',
                  error,
                );
              }
            },
            onError: event => {
              console.error('[useYouTubePlayer] player error', event);
            },
          },
        });
      } catch (error) {
        console.error('[useYouTubePlayer] error creating player', error);
      }
    };

    loadYouTubeIframeApi().then(initPlayer);

    return () => {
      cancelled = true;
      destroyPlayer();
    };
  }, [videoId, mute, hostRef]);
};
